import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Link } from "react-router-dom";
import { Plus, FileText, Users, Video, TrendingDown, AlertTriangle, ShieldCheck, ShieldAlert, Download, Cpu } from "lucide-react";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [overview, setOverview] = useState(null);
  const [videoOps, setVideoOps] = useState([]);
  const [hardest, setHardest] = useState([]);
  useEffect(() => {
    (async () => {
      const [o, v, h] = await Promise.all([
        api.get("/analytics/admin/overview"),
        api.get("/analytics/admin/video-opportunities"),
        api.get("/analytics/admin/hardest"),
      ]);
      setOverview(o.data); setVideoOps(v.data); setHardest(h.data);
    })();
  }, []);
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Yönetim Paneli</h1>
          <p className="text-slate-400 text-sm">Matematik Koçu · sistem özeti</p>
        </div>
        <Link to="/admin/sorular/yeni" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#EA580C] hover:bg-[#F97316] text-white">
          <Plus size={16}/> Soru Ekle
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card icon={<Users size={16}/>} label="Öğrenci" value={overview?.total_students ?? 0}/>
        <Card icon={<FileText size={16}/>} label="Aktif Soru" value={overview?.total_questions ?? 0}/>
        <Card icon={<TrendingDown size={16}/>} label="Denenen" value={overview?.total_attempts ?? 0}/>
        <Card icon={<Video size={16}/>} label="Tamamlanan Oturum" value={overview?.total_sessions ?? 0}/>
        <IntegrityCard />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Panel title="Video Çekilecek Sorular" cta={{ to: "/admin/video-firsatlari", label: "Tümü" }}>
          {videoOps.slice(0, 5).map((r) => (
            <Row key={r.question_id} ref_={r.ref} text={r.text} right={`%${r.error_rate} yanlış · ${r.attempts} deneme`} />
          ))}
          {videoOps.length === 0 && <Empty label="Henüz kritik soru yok."/>}
        </Panel>
        <Panel title="En Zor Sorular">
          {hardest.slice(0, 5).map((r) => (
            <Row key={r.question_id} ref_={r.ref} text={r.text} right={`%${r.accuracy} başarı · ${r.attempts} deneme`} />
          ))}
          {hardest.length === 0 && <Empty label="Yeterli veri yok."/>}
        </Panel>
      </div>

      <ClaudeExportCard />

      <AICoachUsageCard />

      <DevResetCard />
    </div>
  );
}

/**
 * AI Coach Kullanımı — gerçek telemetriden Python/Cache/GPT/Fallback dağılımı.
 * Admin-only backend verisiyle beslenir; sayısal örnek/sabit DEĞİL.
 */
function AICoachUsageCard() {
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(null);
  React.useEffect(() => { (async () => {
    try { const { data } = await api.get("/analytics/admin/explanation-usage"); setData(data); }
    catch (e) { setErr(e?.response?.data?.detail || "Veri alınamadı"); }
  })(); }, []);

  const Row = ({ win, label }) => {
    if (!win) return null;
    const bs = win.by_source || {};
    const pill = (name, n, hint) => (
      <div className="flex flex-col items-start p-3 rounded-lg bg-[#0A0A0A] border border-[#262626] min-w-[120px]" title={hint}>
        <div className="text-xs text-slate-500">{name}</div>
        <div className="text-2xl font-display">{n}</div>
      </div>
    );
    return (
      <div data-testid={`ai-coach-usage-${label}`} className="space-y-2">
        <div className="text-xs text-slate-400 uppercase tracking-widest">{label}</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {pill("Python", bs.python ?? 0, "Açıklama Python şablonundan üretildi (GPT çağrılmadı)")}
          {pill("Cache", bs.cache ?? 0, "Önceden arşivlenmiş GPT açıklaması tekrar kullanıldı")}
          {pill("GPT", bs.gpt ?? 0, "Yeni GPT çağrısı; sanitize kontrolünden geçti")}
          {pill("Fallback", bs.fallback ?? 0, "GPT başarısız; güvenli yedek metin kullanıldı")}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
          <KV label="Toplam istek" value={win.total_requests} />
          <KV label="GPT'siz karşılanan" value={`${win.without_gpt_count} (%${win.without_gpt_ratio.toFixed(1)})`} />
          <KV label="GPT çağrısı / başarılı" value={`${win.gpt_calls} / ${win.gpt_success}`} />
        </div>
      </div>
    );
  };

  return (
    <section data-testid="admin-ai-coach-usage-card"
             className="bg-[#171717] border border-[#262626] rounded-lg p-4">
      <div className="flex items-center gap-2 font-display text-lg mb-1">
        <Cpu size={16} className="text-[#4DA3FF]" /> AI Coach Kullanımı
      </div>
      <p className="text-sm text-slate-400 mb-3">
        Bugünkü Öneri açıklamalarının kaynak dağılımı. Python ve Cache GPT'siz
        karşılanan isteklerdir; GPT yalnız Python şablonu olmayan durumlarda çağrılır.
      </p>
      {err && <div className="text-sm text-rose-400">{err}</div>}
      {data && (
        <div className="space-y-4">
          <Row win={data.last_7_days} label="Son 7 Gün" />
          <Row win={data.last_30_days} label="Son 30 Gün" />
          <div className="text-xs text-slate-500">
            Arşivlenmiş GPT açıklaması: <span className="text-slate-300">{data.archive_size}</span>
          </div>
        </div>
      )}
    </section>
  );
}

function KV({ label, value }) {
  return (
    <div className="p-2 rounded-lg bg-[#0A0A0A] border border-[#262626]">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-sm text-slate-200">{value}</div>
    </div>
  );
}

function ClaudeExportCard() {
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // {found: number, missing: string[]}

  const doExport = async () => {
    // Boşlukları temizle, boş girdileri at, tekrarları tekle indir, sırayı koru.
    const seen = new Set();
    const refs = [];
    for (const part of raw.split(/[,\n;]+/)) {
      const s = part.trim();
      if (s && !seen.has(s)) { seen.add(s); refs.push(s); }
    }
    if (!refs.length) { toast.error("En az bir MAT numarası girin (örn. 0065)"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/questions/admin/export-for-claude", { refs });
      const missing = data.missing || [];
      if (!data.found_count) {
        setResult({ found: 0, missing });
        toast.error(`Hiçbir soru bulunamadı${missing.length ? `: ${missing.join(", ")}` : ""}`);
        return;
      }
      const payload = {
        source: "YZ Matematik",
        purpose: "Claude için benzer becerilerde yeni soru üretimi",
        exported_at: new Date().toISOString(),
        questions: data.questions,
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json;charset=utf-8" });
      const d = new Date();
      const fname = `yz-matematik-claude-sorular-${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}.json`;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fname;
      a.click();
      URL.revokeObjectURL(a.href);
      setResult({ found: data.found_count, missing });
      toast.success(missing.length
        ? `${data.found_count} soru bulundu. ${missing.length} MAT numarası bulunamadı: ${missing.join(", ")}`
        : `${data.found_count} soru bulundu ve JSON olarak hazırlandı.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export başarısız");
    } finally { setBusy(false); }
  };

  return (
    <section data-testid="admin-claude-export-card"
             className="bg-[#171717] border border-[#262626] rounded-lg p-4">
      <div className="flex items-center gap-2 font-display text-lg mb-1">
        <Download size={16} className="text-[#EA580C]" /> Claude için Soru JSON Export
      </div>
      <p className="text-sm text-slate-400 mb-3">
        Virgülle ayrılmış MAT numaralarını girin (örn. <span className="font-mono">0065, 0125, 0178</span>).
        Sorular girdiğiniz sırada, içerik ve metadata'sıyla JSON olarak indirilir.
      </p>
      <textarea
        data-testid="admin-claude-export-input"
        rows={2}
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        placeholder="0065, 0125, 0178, 1447, 1698"
        className="w-full px-3 py-2 rounded-md bg-[#0A0A0A] border border-[#262626] focus:border-[#EA580C] font-mono text-sm outline-none"
      />
      <div className="flex items-center gap-3 mt-3">
        <button onClick={doExport} disabled={busy} data-testid="admin-claude-export-btn"
                className="px-4 py-2 rounded-lg bg-[#EA580C] hover:bg-[#F97316] text-white text-sm disabled:opacity-60">
          {busy ? "Hazırlanıyor…" : "JSON İndir"}
        </button>
        {result && (
          <div data-testid="admin-claude-export-result" className="text-sm">
            {result.found > 0 && (
              <span className="text-emerald-400">{result.found} soru bulundu ve JSON olarak hazırlandı.</span>
            )}
            {result.missing.length > 0 && (
              <span data-testid="admin-claude-export-missing" className="text-rose-400 ml-2">
                {result.missing.length} MAT numarası bulunamadı: {result.missing.join(", ")}
              </span>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

/**
 * Tehlikeli Bölge — komple geliştirme verisi sıfırlama.
 * Akış: Uyarı → Devam → Email OTP → OTP doğrulama → Son onay → kalıcı silme.
 * OTP olmadan backend reset endpoint'i çalışmaz (tek tıkla sıfırlama YOK).
 */
function DevResetCard() {
  const [step, setStep] = useState(null); // null | "warn" | "otp" | "final"
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const close = () => { setStep(null); setCode(""); };

  const requestOtp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/dev-reset/request");
      setEmail(data.email || "");
      if (!data.email_sent) toast.warning("E-posta gönderilemedi — backend loglarını kontrol edin.");
      else toast.success("Doğrulama kodu e-postana gönderildi");
      setStep("otp");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kod gönderilemedi");
    } finally { setBusy(false); }
  };

  const verifyOtp = async () => {
    setBusy(true);
    try {
      await api.post("/admin/dev-reset/confirm", { code, confirm: false });
      setStep("final");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kod doğrulanamadı");
    } finally { setBusy(false); }
  };

  const executeReset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/dev-reset/confirm", { code, confirm: true });
      toast.success("Geliştirme verileri kalıcı olarak sıfırlandı");
      console.log("dev-reset report:", data.report);
      close();
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sıfırlama başarısız");
      setBusy(false);
    }
  };

  return (
    <section data-testid="admin-danger-zone"
             className="border border-rose-500/30 rounded-lg p-4 bg-rose-500/5">
      <div className="flex items-center gap-2 text-rose-300 font-display text-lg mb-1">
        <AlertTriangle size={18}/> Tehlikeli Bölge
      </div>
      <p className="text-sm text-slate-400 mb-3">
        Tüm sorular, konular, alt konular, etiketler ve öğrenci çözüm/istatistik verileri silinir.
        Kullanıcı hesapları korunur. Soru sayacı MAT-0001'e döner.
      </p>
      <button onClick={() => setStep("warn")} data-testid="admin-dev-reset-btn"
              className="px-4 py-2 rounded-lg border border-rose-500/50 text-rose-300 hover:bg-rose-500/10 text-sm">
        Geliştirme Verilerini Temizle
      </button>

      {step && (
        <div data-testid="dev-reset-modal"
             className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-[#171717] border border-rose-500/40 rounded-xl p-6">
            {step === "warn" && (
              <>
                <div className="font-display text-lg text-rose-300 mb-2">⚠️ DİKKAT</div>
                <p className="text-sm text-slate-300 mb-5">
                  Bu işlem tüm soru, konu, alt konu, tag ve ilgili test/istatistik verilerini
                  kalıcı olarak silecektir. <b>Bu işlem geri alınamaz.</b> Devam edersen
                  admin hesabının kayıtlı e-posta adresine tek kullanımlık doğrulama kodu gönderilecek.
                </p>
                <div className="flex gap-2 justify-end">
                  <button onClick={close} data-testid="dev-reset-cancel-btn"
                          className="px-4 py-2 rounded-lg border border-[#262626] text-sm">İptal</button>
                  <button onClick={requestOtp} disabled={busy} data-testid="dev-reset-continue-btn"
                          className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-sm disabled:opacity-60">
                    {busy ? "Gönderiliyor…" : "Devam"}
                  </button>
                </div>
              </>
            )}
            {step === "otp" && (
              <>
                <div className="font-display text-lg mb-2">E-posta Doğrulama</div>
                <p className="text-sm text-slate-400 mb-3">
                  <b>{email}</b> adresine 6 haneli kod gönderildi (10 dk geçerli, tek kullanımlık).
                </p>
                <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                       data-testid="dev-reset-otp-input" placeholder="6 haneli kod" inputMode="numeric"
                       className="w-full px-3 py-2.5 rounded-md bg-[#0A0A0A] border border-[#262626] font-mono text-center text-xl tracking-[0.5em] mb-4"/>
                <div className="flex gap-2 justify-end">
                  <button onClick={close} className="px-4 py-2 rounded-lg border border-[#262626] text-sm">İptal</button>
                  <button onClick={verifyOtp} disabled={busy || code.length !== 6} data-testid="dev-reset-verify-btn"
                          className="px-4 py-2 rounded-lg bg-[#EA580C] hover:bg-[#F97316] text-white text-sm disabled:opacity-60">
                    {busy ? "Doğrulanıyor…" : "Doğrula"}
                  </button>
                </div>
              </>
            )}
            {step === "final" && (
              <>
                <div className="font-display text-lg text-rose-300 mb-2">⚠️ KALICI SİLME</div>
                <p className="text-sm text-slate-300 mb-5">
                  Tüm geliştirme verileri kalıcı olarak silinecek. <b>Bu işlem geri alınamaz.</b>
                </p>
                <div className="flex gap-2 justify-end">
                  <button onClick={close} className="px-4 py-2 rounded-lg border border-[#262626] text-sm">İptal</button>
                  <button onClick={executeReset} disabled={busy} data-testid="dev-reset-final-confirm-btn"
                          className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-sm font-medium disabled:opacity-60">
                    {busy ? "Siliniyor…" : "KALICI OLARAK SIFIRLA"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
function Card({ icon, label, value }) {
  return (
    <div className="bg-[#171717] border border-[#262626] rounded-lg p-4">
      <div className="flex items-center justify-between text-slate-400 text-xs mb-2"><span>{label}</span>{icon}</div>
      <div className="font-display text-2xl">{value}</div>
    </div>
  );
}

/**
 * Veri Bütünlüğü rozeti — GET /questions/admin/integrity-report (salt-okunur rapor).
 * 0 sorun → yeşil; sorun varsa → kırmızı; tıklayınca kategori + referans modalı.
 * API hatası dashboard'u çökertmez; kart "kontrol edilemedi" durumuna düşer.
 */
const INTEGRITY_LABELS = [
  ["missing_topic", "Eksik Konu"],
  ["orphan_topic", "Yetim Konu"],
  ["orphan_subtopic", "Yetim Alt Konu"],
  ["subtopic_topic_mismatch", "Konu/Alt Konu Uyumsuzluğu"],
  ["exam_level_mismatch", "Sınav Seviyesi Uyumsuzluğu"],
];

function IntegrityCard() {
  const [report, setReport] = useState(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/questions/admin/integrity-report");
        setReport(data);
      } catch {
        setFailed(true);
      }
    })();
  }, []);

  const total = report ? Object.values(report.summary || {}).reduce((a, b) => a + b, 0) : null;
  const ok = total === 0;

  const tone = failed
    ? "border-[#262626] text-slate-400"
    : ok
      ? "border-emerald-500/40 text-emerald-300"
      : "border-rose-500/50 text-rose-300";

  return (
    <>
      <button type="button" onClick={() => !failed && setOpen(true)}
              data-testid="admin-integrity-card"
              className={`text-left bg-[#171717] border rounded-lg p-4 transition-colors ${tone} ${failed ? "cursor-default" : "hover:bg-[#1c1c1c] cursor-pointer"}`}>
        <div className="flex items-center justify-between text-xs mb-2 opacity-80">
          <span>Veri Bütünlüğü</span>
          {failed ? <ShieldAlert size={16}/> : ok ? <ShieldCheck size={16}/> : <ShieldAlert size={16}/>}
        </div>
        <div className="font-display text-2xl">
          {failed ? "—" : total === null ? "…" : `${total} sorun`}
        </div>
        <div className="text-[11px] mt-1 opacity-70">
          {failed ? "Veri bütünlüğü kontrol edilemedi" : ok ? "Tüm bağlantılar tutarlı" : "Detay için tıkla"}
        </div>
      </button>

      {open && report && (
        <div data-testid="admin-integrity-modal"
             className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => setOpen(false)}>
          <div className="w-full max-w-md bg-[#171717] border border-[#262626] rounded-xl p-5 max-h-[80vh] overflow-auto"
               onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-lg">Veri Bütünlüğü Raporu</div>
              <button onClick={() => setOpen(false)} data-testid="admin-integrity-close"
                      className="text-xs px-2 py-1 rounded border border-[#262626] hover:border-[#EA580C]">Kapat</button>
            </div>
            <div className="space-y-3">
              {INTEGRITY_LABELS.map(([key, label]) => {
                const count = report.summary?.[key] ?? 0;
                const refs = (report.issues?.[key] || []).map((x) => x.ref).filter(Boolean);
                return (
                  <div key={key} data-testid={`admin-integrity-row-${key}`}
                       className="border-b border-[#262626] last:border-0 pb-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-300">{label}</span>
                      <span className="inline-flex items-center gap-2">
                        {count > 0 && (report.issues?.[key] || []).some((x) => x.ref) && (
                          <Link to={`/admin/sorular?search=${encodeURIComponent(report.issues[key].find((x) => x.ref).ref)}`}
                                data-testid={`admin-integrity-search-${key}`}
                                onClick={() => setOpen(false)}
                                className="text-[11px] text-[#EA580C] hover:underline">
                            Sorular ekranında ara
                          </Link>
                        )}
                        <span className={`font-mono ${count > 0 ? "text-rose-300" : "text-emerald-400"}`}>{count}</span>
                      </span>
                    </div>
                    {count > 0 && refs.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {refs.slice(0, 20).map((r) => (
                          <Link key={r} to={`/admin/sorular?search=${encodeURIComponent(r)}`}
                                data-testid={`admin-integrity-ref-${r}`}
                                onClick={() => setOpen(false)}
                                className="text-[11px] font-mono px-1.5 py-0.5 rounded bg-[#0A0A0A] border border-[#262626] hover:border-[#EA580C] hover:text-[#FDBA74] transition-colors">{r}</Link>
                        ))}
                        {count > 20 && (
                          <span className="text-[11px] text-slate-500 px-1 py-0.5">+{count - 20} daha</span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <p className="text-[11px] text-slate-500 mt-4">
              Salt-okunur rapor — düzeltme için Sorular ekranında ilgili MAT numarasını açın.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
function Panel({ title, children, cta }) {
  return (
    <div className="bg-[#171717] border border-[#262626] rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display text-lg">{title}</div>
        {cta && <Link to={cta.to} className="text-sm text-[#EA580C] hover:underline">{cta.label}</Link>}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
function Row({ ref_, text, right }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-[#262626] last:border-0">
      <div className="min-w-0">
        <div className="font-mono text-xs text-slate-400">{ref_}</div>
        <div className="text-sm truncate">{text}</div>
      </div>
      <div className="text-xs text-slate-400 font-mono whitespace-nowrap">{right}</div>
    </div>
  );
}
function Empty({ label }) { return <div className="text-sm text-slate-500">{label}</div>; }
